import { format, parse, getDay } from 'date-fns';
import { DownloadIcon, FileSpreadsheet, ArrowLeft, RefreshCw } from 'lucide-react';
import type React from 'react';
import { useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { LogProject } from '../services/timeEntryService';
import { colors } from '../theme/colors';
import { exportToPDF, type GroupedEntry } from '../utils/leistungsnachweisExports';
import { exportLeistungsnachweisExcel } from '../services/timeEntryService';
import { LeistungsnachweisFilters } from './LeistungsnachweisFilters';
import { projectReportSettingService } from '../services/projectReportSettingService';
import { customerReportService } from '../services/customerReportService';
import { useAuth } from '../contexts/AuthContext';

interface LeistungsnachweisPreviewProps {
  logProjectsData: LogProject[];
  initialProject?: string;
  initialMonth?: string;
  initialEmployee?: string;
  showDropdowns?: boolean;
}


const GERMAN_DAY_NAMES = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const GERMAN_MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const LEISTUNGSNACHWEIS_HOURS_STD_COL_PX = 50;
const LEISTUNGSNACHWEIS_REISEZEIT_COL_PX = 65;

function countDescriptionWrappedLines(description: string, charsPerLine: number): number {
  const words = description.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 1;

  let lines = 0;
  let lineLen = 0;

  for (const word of words) {
    if (word.length > charsPerLine) {
      if (lineLen > 0) {
        lines++;
        lineLen = 0;
      }
      lines += Math.max(1, Math.ceil(word.length / charsPerLine));
      const rem = word.length % charsPerLine;
      lineLen = rem === 0 ? 0 : rem;
      continue;
    }

    const need = lineLen === 0 ? word.length : lineLen + 1 + word.length;
    if (need > charsPerLine) {
      lines++;
      lineLen = word.length;
    } else {
      lineLen = need;
    }
  }

  if (lineLen > 0) lines++;
  return Math.max(1, lines);
}

function fixLastPageBodyOverflow<T>(
  pages: T[][],
  bodyBudgetPx: number,
  getRowHeight: (entry: T, globalIndex: number) => number,
  lastPageReservePx: number
): T[][] {
  if (pages.length === 0) return pages;
  const slackPx = 12;

  const globalIndexFor = (pgs: T[][], pageIdx: number, rowIdx: number): number => {
    let sum = 0;
    for (let i = 0; i < pageIdx; i++) sum += pgs[i].length;
    return sum + rowIdx;
  };

  const sumHeightsOnPage = (pgs: T[][], pageIdx: number): number => {
    const start = globalIndexFor(pgs, pageIdx, 0);
    let sum = 0;
    pgs[pageIdx].forEach((entry, rowIdx) => {
      sum += getRowHeight(entry, start + rowIdx);
    });
    return sum;
  };

  const result = pages.map((page) => [...page]);

  for (let iter = 0; iter < 1000; iter++) {
    const lastPageIndex = result.length - 1;
    const sum = sumHeightsOnPage(result, lastPageIndex);
    if (sum + lastPageReservePx <= bodyBudgetPx + slackPx) {
      return result.filter((page) => page.length > 0);
    }
    if (result[lastPageIndex].length <= 1) {
      return result.filter((page) => page.length > 0);
    }
    const moved = result[lastPageIndex].pop()!;
    if (!result[lastPageIndex + 1]) {
      result.push([]);
    }
    result[lastPageIndex + 1].unshift(moved);
  }

  return result.filter((page) => page.length > 0);
}

export const LeistungsnachweisPreview: React.FC<LeistungsnachweisPreviewProps> = ({ 
  logProjectsData,
  initialProject = '',
  initialMonth = '',
  initialEmployee = '',
  showDropdowns = true
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user?.role?.toLowerCase().trim() || '';
  const canViewSalesRate = userRole === 'admin' || ['project manager', 'project_manager', 'assistant', 'deputy', 'finance'].includes(userRole);

  const [selectedProject, setSelectedProject] = useState<string>(initialProject);
  const [selectedMonth, setSelectedMonth] = useState<string>(initialMonth);
  const [selectedEmployee, setSelectedEmployee] = useState<string>(initialEmployee);
  const [selectedOrder, setSelectedOrder] = useState<string>('__NOT_APPLICABLE__');
  const [customOrderNumber, setCustomOrderNumber] = useState<string>('');
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isExportingExcel, setIsExportingExcel] = useState<boolean>(false);
  const [includeTravelTime, setIncludeTravelTime] = useState<boolean>(false);
  const [showIsolatedSummary, setShowIsolatedSummary] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const onRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['log-projects'] });
    queryClient.invalidateQueries({ queryKey: ['log-projects-by-employee'] });
    queryClient.invalidateQueries({ queryKey: ['customer-report-isolated'] });
    queryClient.invalidateQueries({ queryKey: ['project-report-settings'] });
  };

  const handleFilterChange = (project: string, month: string, employee: string) => {
    setSelectedProject(project);
    setSelectedMonth(month);
    setSelectedEmployee(employee);
    setSelectedOrder('__NOT_APPLICABLE__');
    setCustomOrderNumber('');
  };

  const orderDisplay = useMemo(() => {
    if (selectedOrder && selectedOrder !== '__NOT_APPLICABLE__') return selectedOrder;
    if ((selectedOrder === '__NOT_APPLICABLE__' || selectedOrder === '') && customOrderNumber?.trim()) return customOrderNumber.trim();
    if (selectedOrder === '__NOT_APPLICABLE__') return 'Not Applicable';
    return '-';
  }, [selectedOrder, customOrderNumber]);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const tableViewportRef = useRef<HTMLDivElement>(null);
  const measureBodyRef = useRef<HTMLTableSectionElement>(null);
  const signaturesRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [measuredTableWidthPx, setMeasuredTableWidthPx] = useState<number | null>(null);
  const [rowHeights, setRowHeights] = useState<number[] | null>(null);

  const parseDate = (dateStr: string): Date | null => {
    try {
      return parse(dateStr, 'dd.MM.yyyy', new Date());
    } catch {
      return null;
    }
  };

  const getDayName = (date: Date): string => {
    const dayIndex = getDay(date);
    return GERMAN_DAY_NAMES[dayIndex];
  };

  const getMonthYearKey = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatMonthYear = (monthYearKey: string): string => {
    const [year, month] = monthYearKey.split('-');
    const monthIndex = parseInt(month, 10) - 1;
    return `${GERMAN_MONTH_NAMES[monthIndex]} ${year}`;
  };

  const filteredEntries = useMemo(() => {
    if (showDropdowns) {
      if (!selectedProject || !selectedMonth || !selectedEmployee) return [];
      const [employeeId] = selectedEmployee.split('|');
      return logProjectsData.filter((item) => {
        if (item.project !== selectedProject) return false;
        if (item.employee_id !== employeeId) return false;
        const date = parseDate(item.date);
        if (!date) return false;
        if (getMonthYearKey(date) !== selectedMonth) return false;
        if (selectedOrder === '__NOT_APPLICABLE__') {
          if (item.order && item.order !== '-' && item.order !== '') return false;
        } else if (selectedOrder && item.order !== selectedOrder) return false;
        return true;
      });
    } else {
      if (selectedOrder === '__NOT_APPLICABLE__') {
        return logProjectsData.filter((item) => {
          if (item.order && item.order !== '-' && item.order !== '') return false;
          return true;
        });
      } else if (selectedOrder) {
        return logProjectsData.filter((item) => {
          if (item.order !== selectedOrder) return false;
          return true;
        });
      }
      return logProjectsData;
    }
  }, [logProjectsData, selectedProject, selectedMonth, selectedEmployee, selectedOrder, showDropdowns]);

  const getTaskCode = (task: string): string => {
    if (!task) return '';
    const trimmed = task.trim();
    const firstSpaceIndex = trimmed.indexOf(' ');
    if (firstSpaceIndex === -1) {
      return trimmed;
    }
    return trimmed.substring(0, firstSpaceIndex);
  };

  const isTravelTimeTask = (taskCode: string): boolean => {
    return taskCode === '001';
  };

  const formatTask = (task: string): string => {
    if (!task) return '';
    const trimmed = task.trim();
    const firstSpaceIndex = trimmed.indexOf(' ');
    if (firstSpaceIndex === -1) {
      return trimmed;
    }
    const taskCode = trimmed.substring(0, firstSpaceIndex);
    const taskName = trimmed.substring(firstSpaceIndex + 1).trim();
    return taskName ? `${taskCode} - ${taskName}` : taskCode;
  };

  const groupedEntries = useMemo(() => {
    const travelTimeByDate = new Map<string, number>();
    
    filteredEntries.forEach((entry) => {
      const taskCode = getTaskCode(entry.task);
      if (isTravelTimeTask(taskCode) && entry.billable) {
        const date = entry.date;
        const currentTravelTime = travelTimeByDate.get(date) || 0;
        travelTimeByDate.set(date, currentTravelTime + entry.hours);
      }
    });

    const grouped: GroupedEntry[] = [];
    const datesWithNonTravelTasks = new Set<string>();
    
    filteredEntries.forEach((entry) => {
      const taskCode = getTaskCode(entry.task);
      if (isTravelTimeTask(taskCode)) {
        return;
      }
      
      if (!entry.billable) {
        return;
      }
      
      const date = parseDate(entry.date);
      if (!date) return;
      
      datesWithNonTravelTasks.add(entry.date);
      const travelTime = travelTimeByDate.get(entry.date) || 0;
      
      grouped.push({
        date: entry.date,
        day: getDayName(date),
        task: formatTask(entry.task),
        description: entry.description || '',
        hours: entry.hours,
        travelTime: travelTime,
      });
    });

    travelTimeByDate.forEach((travelTime, date) => {
      if (!datesWithNonTravelTasks.has(date) && travelTime > 0) {
        const dateObj = parseDate(date);
        if (dateObj) {
          grouped.push({
            date: date,
            day: getDayName(dateObj),
            task: '',
            description: '',
            hours: 0,
            travelTime: travelTime,
          });
        }
      }
    });
    
    const sorted = grouped.sort((a, b) => {
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      if (!dateA || !dateB) return 0;
      
      const dateComparison = dateA.getTime() - dateB.getTime();
      if (dateComparison !== 0) {
        return dateComparison;
      }
      
      const taskCodeA = getTaskCode(a.task);
      const taskCodeB = getTaskCode(b.task);
      return taskCodeA.localeCompare(taskCodeB);
    });

    const result: GroupedEntry[] = [];
    let lastDate = '';
    
    sorted.forEach((entry) => {
      if (entry.date !== lastDate) {
        result.push({ ...entry });
        lastDate = entry.date;
      } else {
        result.push({ ...entry, travelTime: 0 });
      }
    });
    
    return result;
  }, [filteredEntries]);

  const totals = useMemo(() => {
    const hoursTotal = groupedEntries.reduce((acc, entry) => acc + entry.hours, 0);
    
    const travelTimeByDate = new Map<string, number>();
    filteredEntries.forEach((entry) => {
      const taskCode = getTaskCode(entry.task);
      if (isTravelTimeTask(taskCode) && entry.billable) {
        const date = entry.date;
        const currentTravelTime = travelTimeByDate.get(date) || 0;
        travelTimeByDate.set(date, currentTravelTime + entry.hours);
      }
    });
    
    const travelTimeTotal = Array.from(travelTimeByDate.values()).reduce((acc, hours) => acc + hours, 0);

    return {
      hours: hoursTotal,
      travelTime: travelTimeTotal,
    };
  }, [groupedEntries, filteredEntries]);

  const descriptionColumnWidth = useMemo(() => {
    const mmToPx = 3.779527559;
    const pageWidthPx = 210 * mmToPx;
    const contentPadding = 24;
    const fallbackTableW = pageWidthPx - contentPadding * 2;
    const tableWidthPx = measuredTableWidthPx ?? fallbackTableW;

    const dateColWidth = 90;
    const dayColWidth = 90;
    const taskColWidth = 180;
    const hoursColWidth = LEISTUNGSNACHWEIS_HOURS_STD_COL_PX;
    const travelTimeColWidth = includeTravelTime ? LEISTUNGSNACHWEIS_REISEZEIT_COL_PX : 0;

    const fixedColumnsWidth = dateColWidth + dayColWidth + taskColWidth + hoursColWidth + travelTimeColWidth;
    const descriptionWidth = Math.max(100, tableWidthPx - fixedColumnsWidth);

    return `${descriptionWidth}px`;
  }, [includeTravelTime, measuredTableWidthPx]);

  const estimateRowHeight = (entry: GroupedEntry, tableWidthPx: number, includeTravelTime: boolean): number => {
    const baseRowHeight = 32;
    const cellPaddingVertical = 8;
    const fontSize = 12;
    const lineHeight = fontSize * 1.2;

    if (!entry.description || entry.description.trim() === '') {
      return baseRowHeight;
    }

    const dateColWidth = 90;
    const dayColWidth = 90;
    const taskColWidth = 180;
    const hoursColWidth = LEISTUNGSNACHWEIS_HOURS_STD_COL_PX;
    const travelTimeColWidth = includeTravelTime ? LEISTUNGSNACHWEIS_REISEZEIT_COL_PX : 0;
    const cellPaddingHorizontal = 8;
    const totalFixedColsWidth = dateColWidth + dayColWidth + taskColWidth + hoursColWidth + travelTimeColWidth;
    const totalCellPadding = cellPaddingHorizontal * (includeTravelTime ? 12 : 10);

    const descriptionColWidth = Math.max(100, tableWidthPx - totalFixedColsWidth - totalCellPadding);

    const avgCharWidth = fontSize * 0.55;
    const charsPerLine = Math.max(8, Math.floor((descriptionColWidth / avgCharWidth) * 0.92));

    const actualLines = countDescriptionWrappedLines(entry.description, charsPerLine);

    const contentHeight = actualLines * lineHeight;
    const totalHeight = Math.max(baseRowHeight, contentHeight + (cellPaddingVertical * 2));

    return Math.ceil(totalHeight * 1.15);
  };

  const hasBillableEntries = useMemo(() => {
    return filteredEntries.some((entry) => {
      return entry.billable;
    });
  }, [filteredEntries]);

  const availableOrders = useMemo(() => {
    if (showDropdowns) {
      if (!selectedProject || !selectedMonth || !selectedEmployee) return [];
      const [employeeId] = selectedEmployee.split('|');
      const filtered = logProjectsData.filter((item) => {
        if (item.project !== selectedProject) return false;
        if (item.employee_id !== employeeId) return false;
        const date = parseDate(item.date);
        if (!date) return false;
        return getMonthYearKey(date) === selectedMonth;
      });
      const orderSet = new Set<string>();
      filtered.forEach((item) => {
        if (item.order && item.order !== '-') {
          orderSet.add(item.order);
        }
      });
      return Array.from(orderSet).sort();
    } else {
      const orderSet = new Set<string>();
      logProjectsData.forEach((item) => {
        if (item.order && item.order !== '-') {
          orderSet.add(item.order);
        }
      });
      return Array.from(orderSet).sort();
    }
  }, [logProjectsData, selectedProject, selectedMonth, selectedEmployee, showDropdowns]);

  const effectiveProjectForSetting = useMemo(() => {
    if (selectedProject) return selectedProject;
    if (!showDropdowns && (filteredEntries.length > 0 || logProjectsData.length > 0)) {
      const first = filteredEntries[0] ?? logProjectsData[0];
      return first?.project ?? '';
    }
    return '';
  }, [selectedProject, showDropdowns, filteredEntries, logProjectsData]);

  const projectNumberForSetting = useMemo(
    () => effectiveProjectForSetting.split(' ')[0] || '',
    [effectiveProjectForSetting]
  );

  const { data: projectReportSetting, isError: projectReportSettingError } = useQuery({
    queryKey: ['project-report-settings', projectNumberForSetting],
    queryFn: () => projectReportSettingService.getSetting(projectNumberForSetting),
    enabled: !!projectNumberForSetting,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (!projectNumberForSetting) {
      setIncludeTravelTime(false);
      setShowIsolatedSummary(false);
      return;
    }
    if (projectReportSettingError || !projectReportSetting) {
      setIncludeTravelTime(false);
      setShowIsolatedSummary(false);
      return;
    }
    setIncludeTravelTime(projectReportSetting.include_travel_time);
    setShowIsolatedSummary(projectReportSetting.show_isolated_summary);
  }, [projectNumberForSetting, projectReportSetting, projectReportSettingError]);

  const isolatedReportQueryParams = useMemo(() => {
    const projectNumber = effectiveProjectForSetting.split(' ')[0] || '';
    const employeeId = showDropdowns ? (selectedEmployee ? selectedEmployee.split('|')[0] : '') : (filteredEntries[0]?.employee_id ?? '');
    return { projectNumber, month: selectedMonth, employeeId, order: selectedOrder || '' };
  }, [effectiveProjectForSetting, showDropdowns, selectedEmployee, selectedMonth, selectedOrder, filteredEntries]);

  const { data: isolatedReportData } = useQuery({
    queryKey: ['customer-report-isolated', isolatedReportQueryParams.projectNumber, isolatedReportQueryParams.month, isolatedReportQueryParams.employeeId, isolatedReportQueryParams.order],
    queryFn: () =>
      customerReportService.getIsolatedReportByEmployee(
        isolatedReportQueryParams.projectNumber,
        isolatedReportQueryParams.month,
        isolatedReportQueryParams.employeeId,
        isolatedReportQueryParams.order
      ),
    enabled:
      showIsolatedSummary &&
      !!isolatedReportQueryParams.projectNumber &&
      !!isolatedReportQueryParams.month &&
      !!isolatedReportQueryParams.employeeId,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const summaryData = showIsolatedSummary && isolatedReportData?.show_isolated_summary && isolatedReportData.summary && isolatedReportData.summary.length > 0 ? isolatedReportData : null;

  const calculatePages = useMemo(() => {
    if (groupedEntries.length === 0) {
      return [];
    }

    const mmToPx = 3.779527559;
    const pageHeight = 297;
    const pageHeightPx = pageHeight * mmToPx;
    const pageWidth = 210;
    const pageWidthPx = pageWidth * mmToPx;

    const headerHeightPx = 175;
    const tableHeaderHeightPx = 30;
    const tableTfootHeightPx = 45;
    const signaturesHeightPx = 100;
    const footerHeightPx = 20;
    const contentPadding = 24;
    const contentPaddingPx = contentPadding;
    const fallbackTableWidthPx = pageWidthPx - contentPaddingPx * 2;
    const tableWidthPx = measuredTableWidthPx ?? fallbackTableWidthPx;

    const summaryHeightPx = summaryData ? 200 : 0;
    const lastPageBodyReservePx = signaturesHeightPx + footerHeightPx + summaryHeightPx + tableTfootHeightPx;
    const contentVerticalPaddingPx = contentPadding * 2;
    const heightSafetyMarginPx = 40;
    const pageIndexRowPx = 30;
    const formulaTableBodyBudgetPx =
      pageHeightPx -
      headerHeightPx -
      tableHeaderHeightPx -
      contentVerticalPaddingPx -
      heightSafetyMarginPx -
      pageIndexRowPx;
    const pageTableBodyBudgetPx = formulaTableBodyBudgetPx;

    const useMeasuredHeights =
      rowHeights !== null && rowHeights.length === groupedEntries.length;

    const pages: GroupedEntry[][] = [];
    let currentPageRows: GroupedEntry[] = [];
    let currentPageHeight = pageTableBodyBudgetPx;

    groupedEntries.forEach((entry, index) => {
      const measured = useMeasuredHeights ? rowHeights[index] : undefined;
      const entryHeight =
        measured !== undefined && measured > 0
          ? measured
          : estimateRowHeight(entry, tableWidthPx, includeTravelTime);
      const remainingEntries = groupedEntries.length - index - 1;
      const willBeLastPage = remainingEntries === 0;
      const neededReserved = willBeLastPage ? lastPageBodyReservePx : 0;
      const spaceNeeded = entryHeight + neededReserved;
      const hasEnoughSpace = currentPageHeight >= spaceNeeded;

      if (!hasEnoughSpace && currentPageRows.length > 0) {
        pages.push([...currentPageRows]);
        currentPageRows = [];
        currentPageHeight = pageTableBodyBudgetPx;
        const newNeededReserved = willBeLastPage ? lastPageBodyReservePx : 0;
        const newSpaceNeeded = entryHeight + newNeededReserved;
        if (currentPageHeight >= newSpaceNeeded) {
          currentPageRows.push(entry);
          currentPageHeight -= entryHeight;
          if (willBeLastPage) {
            currentPageHeight -= newNeededReserved;
          }
        } else {
          currentPageRows.push(entry);
          currentPageHeight -= entryHeight;
        }
      } else {
        currentPageRows.push(entry);
        currentPageHeight -= entryHeight;
        if (willBeLastPage) {
          currentPageHeight -= neededReserved;
        }
      }
    });

    if (currentPageRows.length > 0) {
      pages.push(currentPageRows);
    }

    const getRowHeight = (entry: GroupedEntry, globalIndex: number): number => {
      const measured = useMeasuredHeights ? rowHeights[globalIndex] : undefined;
      return measured !== undefined && measured > 0
        ? measured
        : estimateRowHeight(entry, tableWidthPx, includeTravelTime);
    };

    return fixLastPageBodyOverflow(pages, pageTableBodyBudgetPx, getRowHeight, lastPageBodyReservePx);
  }, [
    groupedEntries,
    includeTravelTime,
    summaryData,
    measuredTableWidthPx,
    rowHeights,
  ]);

  useLayoutEffect(() => {
    if (groupedEntries.length === 0) {
      setMeasuredTableWidthPx(null);
      return;
    }

    let cancelled = false;
    requestAnimationFrame(() => {
      if (cancelled) return;
      const wrap = tableViewportRef.current;
      if (!wrap) return;
      const wPx = Math.max(100, wrap.clientWidth);
      setMeasuredTableWidthPx(wPx);
    });

    return () => {
      cancelled = true;
    };
  }, [groupedEntries, calculatePages.length]);

  useLayoutEffect(() => {
    if (groupedEntries.length === 0) {
      setRowHeights([]);
      return;
    }

    let cancelled = false;

    const readHeights = () => {
      const tbody = measureBodyRef.current;
      if (!tbody || cancelled) return;
      const trs = tbody.querySelectorAll(':scope > tr');
      if (trs.length !== groupedEntries.length) return;
      const next: number[] = [];
      trs.forEach((tr) => {
        next.push(tr.getBoundingClientRect().height);
      });
      if (!cancelled) {
        setRowHeights(next);
      }
    };

    const run = async () => {
      if (typeof document !== 'undefined' && 'fonts' in document && document.fonts?.ready) {
        try {
          await document.fonts.ready;
        } catch {
          /* ignore */
        }
      }
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (!cancelled) readHeights();
      });
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [groupedEntries, descriptionColumnWidth, includeTravelTime, measuredTableWidthPx]);

  const selectedEmployeeData = useMemo(() => {
    if (showDropdowns && !selectedEmployee) return null;
    
    const firstEntry = filteredEntries[0];
    if (!firstEntry) return null;
    
    let employeeId: string;
    let employeeName: string;
    
    if (showDropdowns) {
      [employeeId, employeeName] = selectedEmployee.split('|');
    } else {
      employeeId = firstEntry.employee_id;
      employeeName = firstEntry.employee_name;
    }
    
    // Remove project type "(internal)" or "(external)" from project name (case-insensitive)
    const projectNameWithoutType = firstEntry.project.replace(/\s*\(internal\)|\s*\(external\)/gi, '').trim();

    return {
      employeeId,
      employeeName,
      projectName: projectNameWithoutType,
      orderNumber: orderDisplay,
      month: selectedMonth ? formatMonthYear(selectedMonth) : '',
    };
  }, [selectedEmployee, filteredEntries, selectedMonth, orderDisplay, showDropdowns]);

  const handleExportPDFAutoTable = async () => {
    if (!pdfContainerRef.current || !selectedEmployeeData || !headerRef.current || !tableHeaderRef.current || calculatePages.length === 0) {
      return;
    }
    await exportToPDF(pdfContainerRef, selectedEmployeeData, setIsExporting);
  };

  const handleExportExcel = async () => {
    const employeeId = showDropdowns
      ? (selectedEmployee ? selectedEmployee.split('|')[0] : '')
      : (filteredEntries[0]?.employee_id ?? '');
    if (!employeeId || !selectedMonth) return;
    const [y, m] = selectedMonth.split('-').map(Number);
    if (!y || !m) return;
    const projectNumber = effectiveProjectForSetting.split(' ')[0] || undefined;
    const orderParam = customOrderNumber?.trim() ? customOrderNumber : selectedOrder;
    setIsExportingExcel(true);
    try {
      await exportLeistungsnachweisExcel(employeeId, y, m, projectNumber, orderParam ?? undefined);
    } finally {
      setIsExportingExcel(false);
    }
  };


  return (
    <div style={{ width: '100%' }}>
      {showDropdowns && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}
        >
          <LeistungsnachweisFilters
            logProjectsData={logProjectsData}
            initialProject={initialProject}
            initialMonth={initialMonth}
            initialEmployee={initialEmployee}
            onFilterChange={handleFilterChange}
          />
          <button
            type="button"
            onClick={onRefresh}
            style={{
              backgroundColor: colors.neutral.white,
              color: colors.accent.blue,
              border: `1px solid ${colors.accent.blue}`,
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease',
              height: '100%',
              gap: '0.5rem',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.neutral.grey[100];
              e.currentTarget.style.borderColor = colors.neutral.grey[400];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.neutral.white;
              e.currentTarget.style.borderColor = colors.neutral.grey[300];
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      )}

      {!showDropdowns && (
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => navigate({ to: '/overview' })}
            style={{
              padding: '0.625rem 1.25rem',
              height: '2.5rem',
              backgroundColor: colors.neutral.grey[200],
              color: colors.secondary.text,
              border: `1px solid ${colors.neutral.grey[300]}`,
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s, box-shadow 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.neutral.grey[300];
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.neutral.grey[200];
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            }}
          >
            <ArrowLeft size={18} />
            Zurück
          </button>
          <button
            type="button"
            onClick={onRefresh}
            style={{
              backgroundColor: colors.neutral.white,
              color: colors.accent.blue,
              border: `1px solid ${colors.accent.blue}`,
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease',
              height: '2.5rem',
              gap: '0.5rem',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.neutral.grey[100];
              e.currentTarget.style.borderColor = colors.neutral.grey[400];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.neutral.white;
              e.currentTarget.style.borderColor = colors.neutral.grey[300];
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      )}

      {selectedEmployeeData && hasBillableEntries && groupedEntries.length > 0 ? (
        <div style={{ width: '100%', position: 'relative' }}>
          <div
            style={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '0.75rem',
              alignItems: 'flex-end',
              marginBottom: '0.5rem',
            }}
          >
            <div style={{ gridColumn: '2', minWidth: '200px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: colors.secondary.text,
                  marginBottom: '0.5rem',
                }}
              >
                Bestellnummer
              </label>
              <select
                value={selectedOrder}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedOrder(v);
                  if (v !== '__NOT_APPLICABLE__' && v !== '') setCustomOrderNumber('');
                }}
                disabled={showDropdowns ? (!selectedProject || !selectedMonth || !selectedEmployee) : logProjectsData.length === 0}
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  height: '2.5rem',
                  border: `1px solid ${colors.neutral.grey[300]}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  backgroundColor: (showDropdowns ? (selectedProject && selectedMonth && selectedEmployee) : logProjectsData.length > 0) ? 'white' : colors.neutral.grey[100],
                  cursor: (showDropdowns ? (selectedProject && selectedMonth && selectedEmployee) : logProjectsData.length > 0) ? 'pointer' : 'not-allowed',
                }}
                onFocus={(e) => {
                  if (showDropdowns ? (selectedProject && selectedMonth && selectedEmployee) : logProjectsData.length > 0) {
                    e.target.style.borderColor = colors.accent.blue;
                  }
                }}
                onBlur={(e) => (e.target.style.borderColor = colors.neutral.grey[300])}
              >
                <option value="__NOT_APPLICABLE__">Not Applicable</option>
                {availableOrders.map((order) => (
                  <option key={order} value={order}>
                    {order}
                  </option>
                ))}
              </select>
            </div>
            {(selectedOrder === '__NOT_APPLICABLE__' || selectedOrder === '') && (
              <div style={{ gridColumn: '2', minWidth: '200px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: colors.secondary.text,
                    marginBottom: '0.5rem',
                  }}
                >
                  Overwrite order number
                </label>
                <input
                  type="text"
                  value={customOrderNumber}
                  onChange={(e) => setCustomOrderNumber(e.target.value)}
                  placeholder="Enter custom order number"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    height: '2.5rem',
                    border: `1px solid ${colors.neutral.grey[300]}`,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    outline: 'none',
                    backgroundColor: 'white',
                  }}
                />
              </div>
            )}
            <div style={{ gridColumn: '3', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              onClick={handleExportPDFAutoTable}
              disabled={isExporting}
              style={{
                padding: '0.625rem 1.25rem',
                height: '2.5rem',
                backgroundColor: isExporting ? colors.neutral.grey[400] : colors.accent.blue,
                color: colors.neutral.white,
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: isExporting ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s, box-shadow 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
              }}
              onMouseEnter={(e) => {
                if (!isExporting) {
                  e.currentTarget.style.backgroundColor = colors.accent.blueHover;
                  e.currentTarget.style.boxShadow = '0 6px 8px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isExporting) {
                  e.currentTarget.style.backgroundColor = colors.accent.blue;
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)';
                }
              }}
            >
              {isExporting ? (
                <>
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      border: `2px solid ${colors.neutral.white}`,
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                  PDF wird erstellt...
                </>
              ) : (
                <>
                  <DownloadIcon size={18} />
                  Als PDF exportieren
                </>
              )}
            </button>
            <button
              onClick={handleExportExcel}
              disabled={isExportingExcel || (showDropdowns ? !(selectedProject && selectedMonth && selectedEmployee) : logProjectsData.length === 0)}
              style={{
                padding: '0.625rem 1.25rem',
                height: '2.5rem',
                backgroundColor: isExportingExcel ? colors.neutral.grey[400] : '#217346',
                color: colors.neutral.white,
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: isExportingExcel ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
              }}
            >
              {isExportingExcel ? (
                <>
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      border: `2px solid ${colors.neutral.white}`,
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                  Excel wird erstellt...
                </>
              ) : (
                <>
                  <FileSpreadsheet size={18} />
                  Als Excel exportieren
                </>
              )}
            </button>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              padding: '2rem 0',
              gap: '2rem',
            }}
          >
            {groupedEntries.length > 0 && (
              <div
                aria-hidden
                style={{
                  position: 'fixed',
                  left: -10000,
                  top: 0,
                  visibility: 'hidden',
                  pointerEvents: 'none',
                  zIndex: -1,
                  width: measuredTableWidthPx != null ? `${measuredTableWidthPx}px` : '210mm',
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ padding: '1.5rem', boxSizing: 'border-box' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      tableLayout: 'fixed',
                    }}
                  >
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${colors.neutral.grey[300]}` }}>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '0.5rem',
                            color: colors.secondary.text,
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            width: '90px',
                            verticalAlign: 'top',
                          }}
                        >
                          Datum
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '0.5rem',
                            color: colors.secondary.text,
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            width: '90px',
                            verticalAlign: 'top',
                          }}
                        >
                          Tag
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '0.5rem',
                            color: colors.secondary.text,
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            width: '180px',
                            verticalAlign: 'top',
                          }}
                        >
                          Task
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '0.5rem',
                            color: colors.secondary.text,
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            width: descriptionColumnWidth,
                            verticalAlign: 'top',
                          }}
                        >
                          Beschreibung
                        </th>
                        <th
                          style={{
                            textAlign: 'right',
                            padding: '0.5rem',
                            color: colors.secondary.text,
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            width: `${LEISTUNGSNACHWEIS_HOURS_STD_COL_PX}px`,
                            verticalAlign: 'top',
                          }}
                        >
                          Stunden (Std.)
                        </th>
                        {includeTravelTime && (
                          <th
                            style={{
                              textAlign: 'right',
                              padding: '0.5rem',
                              color: colors.secondary.text,
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              width: `${LEISTUNGSNACHWEIS_REISEZEIT_COL_PX}px`,
                              verticalAlign: 'top',
                            }}
                          >
                            Reisezeit (Std.)
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody ref={measureBodyRef}>
                      {groupedEntries.map((entry, globalIndex) => {
                        const showDate =
                          globalIndex === 0 ||
                          (globalIndex > 0 && groupedEntries[globalIndex - 1].date !== entry.date);
                        return (
                          <tr
                            key={`measure-${entry.date}-${globalIndex}`}
                            style={{
                              borderBottom: `1px solid ${colors.neutral.grey[200]}`,
                            }}
                          >
                            <td style={{ padding: '0.5rem', fontSize: '0.75rem', width: '90px', verticalAlign: 'top' }}>
                              {showDate ? entry.date : ''}
                            </td>
                            <td style={{ padding: '0.5rem', fontSize: '0.75rem', width: '90px', verticalAlign: 'top' }}>
                              {showDate ? entry.day : ''}
                            </td>
                            <td
                              style={{
                                padding: '0.5rem',
                                color: '#9DB4C0',
                                fontSize: '0.75rem',
                                width: '180px',
                                verticalAlign: 'top',
                              }}
                            >
                              {entry.task}
                            </td>
                            <td
                              style={{
                                padding: '0.5rem',
                                fontSize: '0.75rem',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'normal',
                                verticalAlign: 'top',
                                width: descriptionColumnWidth,
                              }}
                            >
                              {entry.description}
                            </td>
                            <td
                              style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', width: `${LEISTUNGSNACHWEIS_HOURS_STD_COL_PX}px`, verticalAlign: 'top' }}
                            >
                              {entry.hours.toFixed(2)}
                            </td>
                            {includeTravelTime && (
                              <td
                                style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', width: `${LEISTUNGSNACHWEIS_REISEZEIT_COL_PX}px`, verticalAlign: 'top' }}
                              >
                                {showDate ? entry.travelTime.toFixed(2) : ''}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {calculatePages.map((pageRows, pageIndex) => {
              const isLastPage = pageIndex === calculatePages.length - 1;
              const pageRef = pageIndex === 0 ? pdfContainerRef : null;
              const firstPageRow = pageRows[0];
              const lastPageRow = pageRows[pageRows.length - 1];
              const pageKey = `page-${firstPageRow?.date ?? 'empty'}-${firstPageRow?.task ?? 'empty'}-${lastPageRow?.date ?? 'empty'}-${lastPageRow?.task ?? 'empty'}-${pageRows.length}`;

              return (
                <div
                  key={pageKey}
                  ref={pageRef}
                  data-pdf-export="true"
                  style={{
                    width: '210mm',
                    height: '297mm',
                    backgroundColor: colors.neutral.white,
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    padding: '0',
                    margin: '0',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >

                  <div
                    ref={headerRef}
                    style={{
                      backgroundColor: '#9DB4C0',
                      padding: '2rem',
                      borderRadius: '0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <img
                          src="/assets/logo/PPE-intelligence_Logo_rgb_Black.svg"
                          alt="PPF Intelligence Logo"
                          style={{ height: '40px', marginBottom: '0.5rem' }}
                        />
                        <h1 style={{ color: 'white', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                          Leistungsnachweis
                        </h1>
                      </div>
                      <div style={{ color: 'white', textAlign: 'right', fontSize: '0.875rem' }}>
                        <div style={{ marginBottom: '0.375rem' }}>
                          <span style={{ opacity: 0.8 }}>Name:</span> <strong>{selectedEmployeeData.employeeName}</strong>
                        </div>
                        <div style={{ marginBottom: '0.375rem' }}>
                          <span style={{ opacity: 0.8 }}>Projektname:</span>{' '}
                          <strong>{selectedEmployeeData.projectName}</strong>
                        </div>
                        <div style={{ marginBottom: '0.375rem' }}>
                          <span style={{ opacity: 0.8 }}>Bestellnummer:</span>{' '}
                          <strong>{orderDisplay}</strong>
                        </div>
                        <div>
                          <span style={{ opacity: 0.8 }}>Monat:</span> <strong>{selectedEmployeeData.month}</strong>
                        </div>
                      </div>
                    </div>
                  </div>


                  <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                    <div
                      ref={pageIndex === 0 ? tableViewportRef : undefined}
                      style={{ overflow: 'visible', flex: '0 1 auto', minHeight: 0 }}
                    >
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          tableLayout: 'fixed',
                        }}
                      >
                        <thead
                          ref={pageIndex === 0 ? tableHeaderRef : undefined}
                          style={{
                            display: 'table-header-group',
                            position: 'relative',
                          }}
                        >
                          <tr style={{ borderBottom: `2px solid ${colors.neutral.grey[300]}` }}>
                            <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', width: '90px', verticalAlign: 'top' }}>Datum</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', width: '90px', verticalAlign: 'top' }}>Tag</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', width: '180px', verticalAlign: 'top' }}>Task</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', width: descriptionColumnWidth, verticalAlign: 'top' }}>
                              Beschreibung
                            </th>
                            <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', width: `${LEISTUNGSNACHWEIS_HOURS_STD_COL_PX}px`, verticalAlign: 'top' }}>
                              Stunden (Std.)
                            </th>
                            {includeTravelTime && (
                              <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600', width: `${LEISTUNGSNACHWEIS_REISEZEIT_COL_PX}px`, verticalAlign: 'top' }}>
                                Reisezeit (Std.)
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody ref={pageIndex === 0 ? tableBodyRef : undefined}>
                          {pageRows.map((entry, rowIndex) => {
                            const globalIndex = calculatePages.slice(0, pageIndex).reduce((sum, p) => sum + p.length, 0) + rowIndex;
                            const showDate =
                              globalIndex === 0 ||
                              (globalIndex > 0 && groupedEntries[globalIndex - 1].date !== entry.date);
                            return (
                              <tr
                                key={`${entry.date}-${globalIndex}`}
                                style={{
                                  borderBottom: `1px solid ${colors.neutral.grey[200]}`,
                                }}
                              >
                                <td style={{ padding: '0.5rem', fontSize: '0.75rem', width: '90px', verticalAlign: 'top' }}>{showDate ? entry.date : ''}</td>
                                <td style={{ padding: '0.5rem', fontSize: '0.75rem', width: '90px', verticalAlign: 'top' }}>{showDate ? entry.day : ''}</td>
                                <td style={{ padding: '0.5rem', color: '#9DB4C0', fontSize: '0.75rem', width: '180px', verticalAlign: 'top' }}>{entry.task}</td>
                                <td style={{ 
                                  padding: '0.5rem', 
                                  fontSize: '0.75rem',
                                  wordWrap: 'break-word',
                                  overflowWrap: 'break-word',
                                  whiteSpace: 'normal',
                                  verticalAlign: 'top',
                                  width: descriptionColumnWidth
                                }}>{entry.description}</td>
                                <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', width: `${LEISTUNGSNACHWEIS_HOURS_STD_COL_PX}px`, verticalAlign: 'top' }}>{entry.hours.toFixed(2)}</td>
                                {includeTravelTime && (
                                  <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', width: `${LEISTUNGSNACHWEIS_REISEZEIT_COL_PX}px`, verticalAlign: 'top' }}>{showDate ? entry.travelTime.toFixed(2) : ''}</td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                        {isLastPage && (
                          <tfoot>
                            <tr style={{ borderTop: `2px solid ${colors.neutral.grey[300]}` }}>
                              <td colSpan={4} style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem' }}>
                                <strong>Total</strong>
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', width: `${LEISTUNGSNACHWEIS_HOURS_STD_COL_PX}px` }}>
                                <strong>{totals.hours.toFixed(2)}</strong>
                              </td>
                              {includeTravelTime && (
                                <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', width: `${LEISTUNGSNACHWEIS_REISEZEIT_COL_PX}px` }}>
                                  <strong>{totals.travelTime.toFixed(2)}</strong>
                                </td>
                              )}
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>

                    {isLastPage && summaryData && summaryData.summary && summaryData.summary.length > 0 && (
                      <div
                        style={{
                          marginTop: '1rem',
                          marginBottom: '2rem',
                          borderTop: `2px solid ${colors.neutral.grey[200]}`,
                        }}
                      >
                        <p style={{ marginBottom: '0.8rem', color: colors.neutral.grey[700], fontSize: '0.875rem' }}>
                          Zusammenfassung
                        </p>
                        <table
                          style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                          }}
                        >
                          <thead>
                            <tr style={{ borderBottom: `1px solid ${colors.neutral.grey[300]}` }}>
                              <th style={{ textAlign: 'left', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                                Mitarbeiter Name
                              </th>
                              <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                                Billable h
                              </th>
                              {summaryData.include_travel_time && (
                                <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                                  Travel Time Billable h
                                </th>
                              )}
                              <th style={{ textAlign: 'center', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                                Worktype
                              </th>
                              <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                                Verkaufssatz (EUR)
                              </th>
                              <th style={{ textAlign: 'right', padding: '0.5rem', color: colors.secondary.text, fontSize: '0.75rem', fontWeight: '600' }}>
                                Umsatz {summaryData.include_travel_time ? '(inklusive Travel h)' : ''} (EUR)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {summaryData.summary.map((summaryItem: { billable_hours: number; travel_time_hours: number; worktype: string; sales_rate: number; turnover: number }, index: number) => (
                              <tr key={`${summaryItem.worktype}-${summaryItem.sales_rate}-${summaryItem.billable_hours}-${summaryItem.travel_time_hours}-${summaryItem.turnover}`} style={{ borderBottom: `1px solid ${colors.neutral.grey[100]}` }}>
                                <td style={{ padding: '0.5rem', fontSize: '0.75rem' }}>{index === 0 ? (selectedEmployeeData?.employeeName ?? '') : ''}</td>
                                <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem' }}>
                                  {summaryItem.billable_hours.toFixed(2)}
                                </td>
                                {summaryData.include_travel_time && (
                                  <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem' }}>
                                    {summaryItem.travel_time_hours.toFixed(2)}
                                  </td>
                                )}
                                <td style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.75rem' }}>
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      padding: '0.25rem 0.5rem',
                                      borderRadius: '9999px',
                                      fontSize: '0.7rem',
                                      backgroundColor: summaryItem.worktype === 'office' ? colors.accent.blue + '20' : colors.accent.green + '20',
                                      color: summaryItem.worktype === 'office' ? colors.accent.blue : colors.accent.green,
                                    }}
                                  >
                                    {summaryItem.worktype.toUpperCase() || 'N/A'}
                                  </span>
                                </td>
                                <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem' }}>
                                  {canViewSalesRate ? `€${summaryItem.sales_rate.toFixed(2)}` : 'N/A'}
                                </td>
                                <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600' }}>
                                  €{summaryItem.turnover.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{ borderTop: `2px solid ${colors.neutral.grey[300]}` }}>
                              <td colSpan={summaryData.include_travel_time ? 5 : 4} style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.8rem' }}>
                                <strong>Total</strong>
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.8rem', fontWeight: '600' }}>
                                €{summaryData.summary.reduce((acc, s) => acc + s.turnover, 0).toFixed(2)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}

                    {isLastPage && (
                      <div
                        ref={signaturesRef}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '1.5rem',
                          marginTop: 'auto',
                          paddingTop: '5rem',
                          marginBottom: '1rem',
                        }}
                      >

                        <div>
                          <div
                            style={{
                              borderBottom: `1px solid ${colors.neutral.grey[400]}`,
                              paddingBottom: '0.5rem',
                              marginBottom: '0.5rem',
                              height: '48px',
                            }}
                          />
                          <div style={{ fontSize: '0.7rem', color: colors.secondary.text }}>Unterschrift Projektleiter</div>
                        </div>
                        <div>
                          <div
                            style={{
                              borderBottom: `1px solid ${colors.neutral.grey[400]}`,
                              paddingBottom: '0.5rem',
                              marginBottom: '0.5rem',
                              height: '48px',
                            }}
                          />
                          <div style={{ fontSize: '0.7rem', color: colors.secondary.text }}>Unterschrift Kunde</div>
                        </div>
                      </div>
                    )}

                    <div
                      ref={pageIndex === 0 ? footerRef : undefined}
                      style={{
                        marginTop: isLastPage ? '0.375rem' : 'auto',
                        paddingTop: '0.35rem',
                        borderTop: `1px solid ${colors.neutral.grey[200]}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '0.5rem',
                        color: colors.neutral.grey[500],
                        flexShrink: 0,
                      }}
                    >
                      <span>Seite {pageIndex + 1}/{calculatePages.length}</span>
                      <span>Erstellt am: {format(new Date(), 'dd.MM.yyyy HH:mm')} Uhr</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : selectedEmployeeData ? (
        <div
          style={{
            padding: '2rem',
            textAlign: 'center',
            color: colors.secondary.text,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <div>Keine Einträge für die ausgewählten Filter gefunden.</div>
          {!showDropdowns && (
            <button
              onClick={() => navigate({ to: '/overview' })}
              style={{
                padding: '0.625rem 1.25rem',
                height: '2.5rem',
                backgroundColor: colors.neutral.grey[200],
                color: colors.secondary.text,
                border: `1px solid ${colors.neutral.grey[300]}`,
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s, box-shadow 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.neutral.grey[300];
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.neutral.grey[200];
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
              }}
            >
              <ArrowLeft size={18} />
              Zurück
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};

